import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import api, { currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const STEPS = ["requested", "under_review", "quoted", "approved", "in_progress", "completed", "shipped", "delivered"];
const emptyAddress = { label: "Home", full_name: "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "India", is_default: false };

export function StatusStrip({ status }) {
  const idx = STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {STEPS.map((s, i) => (
        <div key={s} className={`overline text-[9px] px-2 py-1 ${i < idx ? "bg-emerald/10 text-emerald" : i === idx ? "bg-emerald text-ivory" : "bg-ivory text-charcoal/40"}`}>
          {s.replace(/_/g, " ")}
        </div>
      ))}
    </div>
  );
}

export default function CustomOrderDetail({ orderId, onClose, isAdmin = false, onChange }) {
  const { user } = useAuth();
  const [co, setCo] = useState(null);
  const [msg, setMsg] = useState("");
  const [quote, setQuote] = useState({ price: "", delivery_days: 14, notes: "" });
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState({ payment_method: "stripe", address: emptyAddress });
  const [progressUrls, setProgressUrls] = useState([""]);
  const [progressCaption, setProgressCaption] = useState("");

  const load = async () => {
    const { data } = await api.get(`/custom-orders/${orderId}`);
    setCo(data);
    setQuote({ price: data.quote_price || "", delivery_days: data.quote_delivery_days || 14, notes: data.quote_notes || "" });
  };
  useEffect(() => { load().catch(() => toast.error("Unable to load request")); }, [orderId]);

  if (!co) return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[80] bg-charcoal/60 flex items-center justify-center">
      <div className="bg-cream p-8">Loading…</div>
    </motion.div>
  );

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    await api.post(`/custom-orders/${co.id}/messages`, { body: msg });
    setMsg("");
    await load();
  };
  const saveQuote = async () => {
    if (!quote.price) return toast.error("Enter a quote price");
    await api.put(`/admin/custom-orders/${co.id}/quote`, { price: Number(quote.price), delivery_days: Number(quote.delivery_days), notes: quote.notes });
    toast.success("Quote sent to customer");
    await load(); onChange && onChange();
  };
  const setStatus = async (s) => {
    await api.put(`/admin/custom-orders/${co.id}/status`, { status: s, note: `Marked as ${s}` });
    toast.success(`Marked ${s.replace(/_/g, " ")}`);
    await load(); onChange && onChange();
  };
  const submitProgress = async () => {
    const imgs = progressUrls.filter(Boolean);
    if (imgs.length === 0) return toast.error("Add at least one image URL");
    await api.post(`/admin/custom-orders/${co.id}/progress`, { images: imgs, caption: progressCaption });
    setProgressUrls([""]); setProgressCaption("");
    toast.success("Progress update sent");
    await load();
  };
  const submitFinal = async () => {
    const imgs = progressUrls.filter(Boolean);
    if (imgs.length === 0) return toast.error("Add final image URLs first");
    await api.post(`/admin/custom-orders/${co.id}/final`, { images: imgs, caption: progressCaption || "Piece completed" });
    setProgressUrls([""]); setProgressCaption("");
    toast.success("Marked complete");
    await load(); onChange && onChange();
  };
  const decline = async () => {
    await api.put(`/custom-orders/${co.id}/decision`, { approved: false });
    toast("Quote declined");
    await load(); onChange && onChange();
  };
  const approveAndPay = async () => {
    if (!decision.address.full_name || !decision.address.line1 || !decision.address.city) return toast.error("Please complete the delivery address");
    try {
      const { data } = await api.put(`/custom-orders/${co.id}/decision`, {
        approved: true,
        payment_method: decision.payment_method,
        origin_url: window.location.origin,
        address: decision.address,
      });
      if (data.payment_method === "cod") {
        toast.success("Approved — production started");
        setDecisionOpen(false); await load(); onChange && onChange();
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Please try again"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-charcoal/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto" data-testid="co-detail">
      <div className="bg-cream w-full max-w-4xl my-8 max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-cream border-b divider-gold flex items-center justify-between p-5 z-10">
          <div>
            <div className="overline text-emerald">Custom request</div>
            <div className="font-serif text-2xl">#{co.request_number}</div>
          </div>
          <button onClick={onClose} className="p-2" data-testid="co-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-8">
          <div><StatusStrip status={co.status} /></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-ivory/50 p-5 border divider-gold">
              <div className="overline text-charcoal/60 mb-3">Brief</div>
              <div className="text-sm space-y-2">
                <div><span className="text-charcoal/50">From:</span> {co.name} · {co.email}{co.phone ? ` · ${co.phone}` : ""}</div>
                <div><span className="text-charcoal/50">Type:</span> {co.product_type}</div>
                {(co.budget_min || co.budget_max) && <div><span className="text-charcoal/50">Budget:</span> {co.budget_min ? currency(co.budget_min) : "—"} – {co.budget_max ? currency(co.budget_max) : "—"}</div>}
                {co.expected_delivery && <div><span className="text-charcoal/50">By:</span> {co.expected_delivery}</div>}
                {co.occasion && <div><span className="text-charcoal/50">Occasion:</span> {co.occasion}</div>}
              </div>
              <p className="mt-4 text-sm leading-relaxed">{co.description}</p>
            </div>
            <div className="bg-ivory/50 p-5 border divider-gold">
              <div className="overline text-charcoal/60 mb-3">References</div>
              {co.reference_images?.length ? (
                <div className="grid grid-cols-3 gap-2">
                  {co.reference_images.map((u, i) => (<a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" className="w-full aspect-square object-cover bg-ivory" /></a>))}
                </div>
              ) : <div className="text-xs text-charcoal/50 italic">No references</div>}
            </div>
          </div>

          {/* Quote */}
          {isAdmin ? (
            <div className="bg-ivory/50 p-5 border divider-gold">
              <div className="overline text-emerald mb-3">Send / update quote</div>
              <div className="grid grid-cols-3 gap-3">
                <Input type="number" placeholder="Price ₹" value={quote.price} onChange={(e) => setQuote({ ...quote, price: e.target.value })} data-testid="co-quote-price" className="bg-cream rounded-none" />
                <Input type="number" placeholder="Days" value={quote.delivery_days} onChange={(e) => setQuote({ ...quote, delivery_days: e.target.value })} data-testid="co-quote-days" className="bg-cream rounded-none" />
                <Button onClick={saveQuote} data-testid="co-quote-save" className="rounded-none bg-emerald hover:bg-emerald/90 text-ivory">Send quote</Button>
              </div>
              <Textarea placeholder="Notes for the customer (materials, options, terms)…" value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} className="mt-3 bg-cream rounded-none" />
            </div>
          ) : co.quote_price ? (
            <div className="bg-ivory/50 p-5 border divider-gold" data-testid="co-quote-view">
              <div className="overline text-emerald mb-3">Our quote</div>
              <div className="flex items-baseline gap-6">
                <div className="font-serif text-3xl">{currency(co.quote_price)}</div>
                <div className="text-sm text-charcoal/70">· {co.quote_delivery_days} days delivery</div>
              </div>
              {co.quote_notes && <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">{co.quote_notes}</p>}
              {co.status === "quoted" && (
                <div className="mt-5 flex gap-3">
                  <Button onClick={() => setDecisionOpen(true)} data-testid="co-approve-open" className="rounded-none bg-emerald hover:bg-emerald/90 text-ivory">Approve & pay</Button>
                  <Button variant="outline" onClick={decline} data-testid="co-decline" className="rounded-none border-charcoal/30">Decline</Button>
                </div>
              )}
              {co.status !== "quoted" && (
                <div className="mt-4 overline text-emerald">
                  {co.status === "rejected" ? "Declined" : co.status === "approved" || co.payment_status === "cod_pending" || co.payment_status === "paid" ? "Approved" : ""}
                </div>
              )}
            </div>
          ) : null}

          {/* Admin status shortcuts */}
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              {["under_review", "in_progress", "shipped", "delivered", "cancelled"].map((s) => (
                <Button key={s} variant="outline" onClick={() => setStatus(s)} className="rounded-none border-charcoal/30 text-xs h-8" data-testid={`co-set-${s}`}>
                  Mark {s.replace(/_/g, " ")}
                </Button>
              ))}
            </div>
          )}

          {/* Progress updates */}
          <div>
            <div className="overline text-emerald mb-3">Studio updates</div>
            {co.progress_updates?.length ? (
              <div className="space-y-4">
                {co.progress_updates.map((u) => (
                  <div key={u.id} className="border-l-2 border-gold pl-4">
                    <div className="text-xs text-charcoal/60">{new Date(u.at).toLocaleString()}</div>
                    {u.caption && <div className="text-sm mt-1">{u.caption}</div>}
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {u.images.map((im, i) => <img key={i} src={im} alt="" className="w-full aspect-square object-cover bg-ivory" />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="text-xs text-charcoal/50 italic">No updates yet</div>}

            {isAdmin && (co.status === "in_progress" || co.status === "approved") && (
              <div className="mt-5 p-4 bg-ivory/40 border divider-gold">
                <div className="overline text-charcoal/60 mb-2">Add progress update</div>
                {progressUrls.map((u, i) => (
                  <Input key={i} placeholder="Image URL (or paste after uploading)" value={u} onChange={(e) => setProgressUrls((arr) => arr.map((x, k) => (k === i ? e.target.value : x)))} className="mb-2 bg-cream rounded-none" data-testid={`co-prog-url-${i}`} />
                ))}
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={() => setProgressUrls((arr) => [...arr, ""])} className="rounded-none">+ Add another</Button>
                  <UploadInline onUploaded={(url) => setProgressUrls((arr) => { const copy = [...arr]; const idx = copy.findIndex((x) => !x); if (idx >= 0) copy[idx] = url; else copy.push(url); return copy; })} />
                </div>
                <Textarea placeholder="Caption (optional)" value={progressCaption} onChange={(e) => setProgressCaption(e.target.value)} className="mt-2 bg-cream rounded-none" />
                <div className="flex gap-2 mt-3">
                  <Button onClick={submitProgress} data-testid="co-prog-submit" className="rounded-none bg-emerald hover:bg-emerald/90 text-ivory">Send progress update</Button>
                  <Button variant="outline" onClick={submitFinal} data-testid="co-final-submit" className="rounded-none">Mark as completed + upload final</Button>
                </div>
              </div>
            )}
          </div>

          {co.final_images?.length > 0 && (
            <div>
              <div className="overline text-emerald mb-3">The finished piece</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {co.final_images.map((im, i) => <img key={i} src={im} alt="" className="w-full aspect-square object-cover bg-ivory" />)}
              </div>
            </div>
          )}

          {/* Messages */}
          <div>
            <div className="overline text-emerald mb-3">Conversation</div>
            <div className="space-y-3 max-h-72 overflow-auto p-4 bg-ivory/40 border divider-gold">
              {co.messages?.length ? co.messages.map((m) => (
                <div key={m.id} className={`flex ${m.author === "admin" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[75%] p-3 text-sm ${m.author === "admin" ? "bg-emerald text-ivory" : "bg-cream border divider-gold"}`}>
                    <div className="text-[10px] opacity-70 mb-1">{m.author === "admin" ? "Studio" : m.author_name || "You"} · {new Date(m.at).toLocaleString()}</div>
                    <div className="whitespace-pre-wrap">{m.body}</div>
                  </div>
                </div>
              )) : <div className="text-xs text-charcoal/50 italic">No messages yet</div>}
            </div>
            <form onSubmit={sendMessage} className="flex gap-2 mt-3">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a message…" data-testid="co-msg-input" className="bg-cream rounded-none" />
              <Button type="submit" data-testid="co-msg-send" className="rounded-none bg-charcoal hover:bg-charcoal/85 text-ivory"><Send className="w-4 h-4" /></Button>
            </form>
          </div>
        </div>

        {/* Approve modal */}
        <AnimatePresence>
          {decisionOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] bg-charcoal/70 flex items-center justify-center p-4">
              <div className="bg-cream w-full max-w-lg p-6" data-testid="co-approve-modal">
                <div className="overline text-emerald mb-2">Approve & pay</div>
                <div className="font-serif text-2xl mb-4">Confirm delivery & payment</div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Full name" required value={decision.address.full_name} onChange={(e) => setDecision({ ...decision, address: { ...decision.address, full_name: e.target.value } })} data-testid="coa-name" />
                  <Input placeholder="Phone" required value={decision.address.phone} onChange={(e) => setDecision({ ...decision, address: { ...decision.address, phone: e.target.value } })} data-testid="coa-phone" />
                  <Input placeholder="Address line 1" required value={decision.address.line1} onChange={(e) => setDecision({ ...decision, address: { ...decision.address, line1: e.target.value } })} className="col-span-2" data-testid="coa-line1" />
                  <Input placeholder="City" required value={decision.address.city} onChange={(e) => setDecision({ ...decision, address: { ...decision.address, city: e.target.value } })} data-testid="coa-city" />
                  <Input placeholder="State" required value={decision.address.state} onChange={(e) => setDecision({ ...decision, address: { ...decision.address, state: e.target.value } })} data-testid="coa-state" />
                  <Input placeholder="Postal code" required value={decision.address.postal_code} onChange={(e) => setDecision({ ...decision, address: { ...decision.address, postal_code: e.target.value } })} data-testid="coa-zip" />
                </div>
                <div className="mt-4">
                  <RadioGroup value={decision.payment_method} onValueChange={(v) => setDecision({ ...decision, payment_method: v })}>
                    <label className="flex items-center gap-3 p-3 border divider-gold cursor-pointer" data-testid="coa-pay-stripe">
                      <RadioGroupItem value="stripe" /> <span className="text-sm">Card · Stripe secured — pay {currency(co.quote_price)} now</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 border divider-gold cursor-pointer" data-testid="coa-pay-cod">
                      <RadioGroupItem value="cod" /> <span className="text-sm">Cash on delivery — pay when the piece arrives</span>
                    </label>
                  </RadioGroup>
                </div>
                <div className="mt-5 flex gap-3">
                  <Button onClick={approveAndPay} data-testid="coa-confirm" className="flex-1 rounded-none bg-emerald hover:bg-emerald/90 text-ivory">Confirm</Button>
                  <Button variant="outline" onClick={() => setDecisionOpen(false)} className="rounded-none">Cancel</Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function UploadInline({ onUploaded }) {
  const [busy, setBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  return (
    <label className="inline-flex items-center gap-2 px-4 py-2 border divider-gold cursor-pointer text-xs uppercase tracking-widest">
      <input type="file" accept="image/*" hidden onChange={async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        setBusy(true);
        try {
          const fd = new FormData(); fd.append("file", file);
          const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          onUploaded(`${BACKEND}${data.url}`);
        } catch { toast.error("Upload failed"); }
        setBusy(false); e.target.value = "";
      }} />
      {busy ? "Uploading…" : "Upload"}
    </label>
  );
}
